import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BiChevronDown, BiLoader } from 'react-icons/bi';
import { BsPrinterFill } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '../../../../state/store';
import { useAskVantage } from '../../context/AskVantageProvider';
import { SenderRole } from '../../helpers/enums';
import type { ThemeConfig } from '../../theme';
import { useTheme } from '../../theme';
import type { AssistantMessage, UserMessage } from '../../types';
import AssistantMessageBubble from '../AssistantMessageBubble';
import AssistantThoughtFeedback from '../AssistantThoughtFeedback';
import InterimAnswer from '../InterimAnswer';
import UserMessageBubble from '../UserMessageBubble';

const ChatContainer: React.FC = () => {
  const {
    messages,
    thinkingState,
    interimAnswer,
    conversations,
    activeConversationId,
    sidebarOpen,
    isConversationLoading,
    isConversationLoadError
  } = useAskVantage();
  const theme = useTheme();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const userMessageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const lastScrollTopRef = useRef(0);
  const isScrollingProgrammatically = useRef(false);
  const previousMessagesLengthRef = useRef(0);
  const username = useSelector((state: RootState) => state.company?.company_details.user_name);

  const isNearBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return false;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 150; // pixels from bottom
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  const scrollToBottom = useCallback((behavior: 'smooth' | 'auto' = 'smooth') => {
    if (messagesEndRef.current && containerRef.current) {
      isScrollingProgrammatically.current = true;
      messagesEndRef.current.scrollIntoView({ behavior });
      // Reset flag after scroll animation completes
      setTimeout(
        () => {
          isScrollingProgrammatically.current = false;
          setShouldAutoScroll(true);
          setShowScrollButton(false);
        },
        behavior === 'smooth' ? 500 : 100
      );
    }
  }, []);

  // Get current conversation title
  const currentConversation = conversations.find((conv) => conv.id === activeConversationId);
  const conversationTitle = currentConversation?.title
    ? currentConversation.title
    : isConversationLoading
      ? 'loading conversation...'
      : isConversationLoadError
        ? 'Error loading conversation'
        : '';

  // Export chat and trigger print
  const exportChat = useCallback(async () => {
    if (messages.length === 0 || isExporting) return;

    setIsExporting(true);
    try {
      // Import the export utilities
      const { chatExporter } = await import('../../helpers/exportUtils');
      const { chartImageExtractor } = await import('../../helpers/chartImageExtractor');

      // Get chart block IDs that need image extraction
      const chartBlockIds = chatExporter.getChartBlockIds(messages);

      // Wait a bit for charts to fully render and register
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Extract chart images
      const chartImages = await chartImageExtractor.extractMultipleChartImages(chartBlockIds);

      // Export to HTML with auto-print
      await chatExporter.exportToHTMLWithPrint(messages, conversationTitle, chartImages, username);
    } catch (error) {
      console.error('Failed to export chat:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [messages, conversationTitle, username, isExporting]);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    const hasNewMessage = messages.length > previousMessagesLengthRef.current;
    const lastMessage = messages[messages.length - 1];
    const isNewUserMessage = hasNewMessage && lastMessage?.message_type === SenderRole.USER;

    // Force scroll to bottom when user sends a new message (even if scrolled up)
    if (isNewUserMessage) {
      setShouldAutoScroll(true);
      scrollToBottom('smooth');
    }
    // Otherwise, only auto-scroll if user is already at bottom
    else if (shouldAutoScroll && messages.length > 0) {
      scrollToBottom('auto');
    }

    previousMessagesLengthRef.current = messages.length;
  }, [messages, shouldAutoScroll, scrollToBottom]);

  // Auto-scroll for thinking state and interim answer
  useEffect(() => {
    if (shouldAutoScroll && (thinkingState || interimAnswer)) {
      scrollToBottom('auto');
    }
  }, [thinkingState, interimAnswer, shouldAutoScroll, scrollToBottom]);

  // Handle scroll events to detect user scrolling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Ignore programmatic scrolls
      if (isScrollingProgrammatically.current) {
        return;
      }

      const { scrollTop } = container;
      const scrollingUp = scrollTop < lastScrollTopRef.current;
      const nearBottom = isNearBottom();

      lastScrollTopRef.current = scrollTop;

      // User scrolled up intentionally -> disable auto-scroll
      if (scrollingUp && !nearBottom) {
        setShouldAutoScroll(false);
        setShowScrollButton(true);
      }

      // User scrolled back to bottom -> enable auto-scroll
      if (nearBottom) {
        setShouldAutoScroll(true);
        setShowScrollButton(false);
      } else if (!shouldAutoScroll) {
        // User is scrolled up -> show button
        setShowScrollButton(true);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isNearBottom, shouldAutoScroll]);

  // Handle keyboard shortcut for export (Ctrl+P / Cmd+P)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+P (Windows/Linux) or Cmd+P (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        // Prevent default browser print dialog
        event.preventDefault();

        // Only trigger export if there are messages
        if (messages.length > 0) {
          exportChat();
        }
      }
    };

    // Add event listener when component mounts
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [messages.length, exportChat]);

  return (
    <Wrapper ref={containerRef} $theme={theme}>
      {/* Fixed Header */}
      <Header $theme={theme} sidebarOpen={sidebarOpen}>
        <HeaderContent>
          <ConversationTitle $theme={theme}>{conversationTitle}</ConversationTitle>
          <ExportButton
            onClick={exportChat}
            $theme={theme}
            disabled={messages.length === 0 || isExporting}
            title={isExporting ? 'Preparing chat for print...' : 'Print chat'}
            $isLoading={isExporting}
          >
            {isExporting ? (
              <BiLoader size={18} className="spinning" />
            ) : (
              <BsPrinterFill size={18} />
            )}
          </ExportButton>
        </HeaderContent>
      </Header>

      <MessagesContainer $theme={theme}>
        {isConversationLoading ? (
          <LoadingState $theme={theme}>
            <LoadingSpinner $theme={theme} />
            <LoadingText>Loading conversation...</LoadingText>
          </LoadingState>
        ) : isConversationLoadError ? (
          <ErrorState $theme={theme}>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorText>{isConversationLoadError}</ErrorText>
          </ErrorState>
        ) : (
          messages.map((message, index) => {
            if (message.message_type === SenderRole.USER) {
              // Find the last user message in the array
              const lastUserMessageIndex = messages
                .map((msg, idx) => ({ msg, idx }))
                .filter(({ msg }) => msg.message_type === SenderRole.USER)
                .pop()?.idx;

              const isLastUserMessage = index === lastUserMessageIndex;

              return (
                <>
                  <div key={message.id} ref={(el) => (userMessageRefs.current[message.id] = el)}>
                    <UserMessageBubble message={message as UserMessage} />
                  </div>
                  {isLastUserMessage && thinkingState && (
                    <AssistantThoughtFeedback
                      key={`${thinkingState}-${Date.now()}`}
                      content={thinkingState}
                      speed={1.5}
                      disabled={false}
                      className="assistant-thought-process"
                    />
                  )}
                  {isLastUserMessage && interimAnswer && (
                    <InterimAnswer key={`${interimAnswer}-${Date.now()}`} />
                  )}
                </>
              );
            } else {
              return (
                <AssistantMessageBubble key={message.id} message={message as AssistantMessage} />
              );
            }
          })
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <ScrollToBottomButton onClick={() => scrollToBottom('smooth')} $theme={theme}>
          <BiChevronDown size={20} />
        </ScrollToBottomButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $theme: ThemeConfig;
}>`
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding-top: 60px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.$theme.colors.messageBoxBorder};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.$theme.colors.sidebarGroupColor};
  }
`;

const MessagesContainer = styled.div<{ $theme: ThemeConfig }>`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$theme.tokens.spaceMd};
  padding: ${(props) => props.$theme.tokens.spaceLg};
  padding-bottom: 120px; /* Space for sticky MessageBox */
  min-height: 100%;
  max-width: 820px;

  @media (max-width: 768px) {
    padding: ${(props) => props.$theme.tokens.spaceMd};
    padding-bottom: 190px; /* Extra space for fixed message box on mobile */
  }
`;

const ScrollToBottomButton = styled.button<{ $theme: ThemeConfig }>`
  position: fixed;
  bottom: 140px; /* Above the message box */
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background-color: ${(props) => props.$theme.colors.messageBoxBg};
  color: ${(props) => props.$theme.colors.mainText};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 100;
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    background-color: ${(props) => props.$theme.colors.primary};
    color: white;
  }

  &:active {
    transform: translateY(0);
  }

  /* Animation when appearing */
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 768px) {
    right: 16px;
    bottom: 140px; /* Position above fixed message box */
    width: 44px;
    height: 44px;
  }
`;

const Header = styled.div<{
  $theme: ThemeConfig;
  sidebarOpen: boolean;
}>`
  position: fixed;
  top: 60px;
  left: 0;
  right: 8px; /* Account for scrollbar width */
  height: 60px;
  background-color: ${(props) => props.$theme.colors.mainBg};
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0 ${(props) => props.$theme.tokens.spaceLg};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all ${(props) => props.$theme.tokens.transitionFast};

  /* Responsive behavior matching MainContent */
  @media (min-width: 769px) {
    left: ${(props) => (props.sidebarOpen ? '320px' : '0')};
    width: ${(props) => (props.sidebarOpen ? 'calc(100% - 322px - 8px)' : 'calc(100% - 8px)')};
  }

  @media (max-width: 1280px) and (min-width: 769px) {
    left: ${(props) => (props.sidebarOpen ? '280px' : '0')};
    width: ${(props) => (props.sidebarOpen ? 'calc(100% - 280px - 8px)' : 'calc(100% - 8px)')};
  }

  @media (max-width: 768px) {
    left: 0;
    width: calc(100% - 8px);
    padding: 0 ${(props) => props.$theme.tokens.spaceMd};
  }
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ConversationTitle = styled.h2<{ $theme: ThemeConfig }>`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.$theme.colors.mainText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
  line-height: 50px;

  @media (max-width: 768px) {
    font-size: 16px !important;
    max-width: 60%;
  }
`;

const ExportButton = styled.button<{ $theme: ThemeConfig; $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  background-color: transparent;
  color: ${(props) => props.$theme.colors.mainText};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  gap: 6px;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.$theme.colors.primary};
    color: white;
    border-color: ${(props) => props.$theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    padding: 6px 8px;
  }
`;

const LoadingState = styled.div<{ $theme: ThemeConfig }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;
  min-height: 400px;
`;

const LoadingSpinner = styled.div<{ $theme: ThemeConfig }>`
  width: 48px;
  height: 48px;
  border: 4px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-top-color: ${(props) => props.$theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div<{ $theme?: ThemeConfig }>`
  color: ${(props) => props.$theme?.colors.sidebarGroupColor || '#888'};
  font-size: 1rem;
`;

const ErrorState = styled.div<{ $theme: ThemeConfig }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
  min-height: 400px;
`;

const ErrorIcon = styled.div`
  font-size: 64px;
`;

const ErrorText = styled.div<{ $theme?: ThemeConfig }>`
  color: ${(props) => props.$theme?.colors.mainText || '#333'};
  font-size: 1rem;
  text-align: center;
  line-height: 1.6;
  max-width: 500px;
`;

export default ChatContainer;
